import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import Preview from '@renderer/components/Preview'
import { flashEffect } from '@renderer/utils/flashUtils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { FaUndo } from 'react-icons/fa'
import { backgroundFilterProcessor } from '@renderer/utils/backgroundFilterProcessor'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { FrameOption, FrameConfig } from '@renderer/types/configState'
import { Recorder, RecorderStatus } from 'canvas-record'
import { AVC } from 'media-codecs'
import { resolveFramedVideoPlaybackAndRemote } from '@renderer/utils/galleryUrlUtils'

const Capture: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const {
    setSessionState,
    userPreferences,
    gallery,
    galleryKey,
    uploadSessionState,
    logActivity,
    userId
  } = useSessionStateStore()
  const { config: globalConfig, frames } = useConfigStore()
  const { downloadLocal, cameraSettings } = useSettingsStore()
  const { fetchSubscriptionAndCredits } = useCreditsStore()

  // Helper function to normalize captureRatio - 'auto' means no cropping
  const getNormalizedCaptureRatio = (): number | undefined => {
    if (cameraSettings.captureRatio === 'auto') return undefined
    if (typeof cameraSettings.captureRatio === 'number') return cameraSettings.captureRatio
    return 4 / 3 // Default fallback
  }

  // Configuration
  const config = globalConfig?.pages.capture
  const captureTimeout = config?.settings?.captureTimeout || 60 * 5 // 10 minutes default
  const initialCaptureCountdown = config?.settings.captureCountdown || 3

  // Get photo count and layout based on selected category
  const getPhotoLayoutInfo = (): { count: number; layout: string } => {
    const cameraMode = userPreferences.cameraMode
    const selectedFrame = frames.find((frame) => frame._id === userPreferences.frameId)

    if (!selectedFrame) {
      console.log('No selected frame found, using default layout')
      return { count: 4, layout: 'grid' }
    }

    console.log('Selected frame:', selectedFrame.name, 'ID:', selectedFrame._id)

    // Try to determine layout based on frame category first
    const frameCategories = useConfigStore.getState().frameCategories || []
    const category = frameCategories.find((cat) =>
      cat.frames?.some((frame) => frame._id === selectedFrame._id)
    )

    if (category) {
      console.log('Found category:', category.name)
      const categoryName = category.name.toLowerCase()
      if (categoryName.includes('3cut')) {
        console.log('Using 3cut layout (3 photos, vertical)')
        return { count: 3, layout: 'vertical' } // 3cut: 1 1, 2 2, 3 3
      } else if (categoryName.includes('4cut1')) {
        console.log('Using 4cut1 layout (4 photos, grid)')
        return { count: 4, layout: 'grid' } // 4cut1: 1 2, 3 4
      } else if (categoryName.includes('4cut2')) {
        console.log('Using 4cut2 layout (4 photos, vertical)')
        return { count: 4, layout: 'vertical' } // 4cut2: 1 1, 2 2, 3 3, 4 4
      }
    }

    // Fallback to frame name if category doesn't match
    const frameName = selectedFrame.name.toLowerCase()
    console.log('No category match, checking frame name:', frameName)

    if (frameName.includes('3cut') || cameraMode === 'basic') {
      console.log('Using 3cut layout based on frame name (3 photos, vertical)')
      return { count: 3, layout: 'vertical' } // 3cut: 1 1, 2 2, 3 3
    } else if (frameName.includes('4cut1')) {
      console.log('Using 4cut1 layout based on frame name (4 photos, grid)')
      return { count: 4, layout: 'grid' } // 4cut1: 1 2, 3 4
    } else if (frameName.includes('4cut2')) {
      console.log('Using 4cut2 layout based on frame name (4 photos, vertical)')
      return { count: 4, layout: 'vertical' } // 4cut2: 1 1, 2 2, 3 3, 4 4
    }

    console.log('Using default layout (4 photos, grid)')
    return { count: 4, layout: 'grid' } // Default
  }

  const photoLayoutInfo = getPhotoLayoutInfo()
  const captureCount = photoLayoutInfo.count

  const [capturedImageURL, setCapturedImageURL] = useState<string | null>(null)
  const [captureCountdown, setCaptureCountdown] = useState<number>(initialCaptureCountdown)
  const [preparing, setPreparing] = useState(userPreferences.retakeCount === 0)
  const [capturing, setCapturing] = useState(false)
  const [remainingTime, setRemainingTime] = useState<string>('10:00')
  const [sourceRatio, setSourceRatio] = useState<number | null>(null)
  const [retakingIndex, setRetakingIndex] = useState<number | null>(null)
  const [allCapturesComplete, setAllCapturesComplete] = useState(false)
  const FPS = 30
  const [canvasDimensions, setCanvasDimensions] = useState<{
    width: number
    height: number
  } | null>(null)
  const captureCanvasRecorderRef = useRef<Recorder | null>(null)

  // Refs
  const stopCaptureRef = useRef<boolean>(false)
  const remainingCapturesRef = useRef<number>(
    userPreferences.retakeCount > 0 ? userPreferences.retakeCount : captureCount
  )

  // Countdown hooks
  const prepCountdownHook = useCountdown(
    userPreferences.retakeCount > 0 ? 1 : config?.settings.prepTimeout || 5
  )
  const captureCountdownHook = useCountdown(config?.settings.captureCountdown || 3, { audioCue: true })
  const timeoutCountdown = useCountdown(captureTimeout)

  // Frame settings
  const displayRatio = userPreferences.framePhotoRatio || 4 / 3

  // Load image and get its ratio when capturedImageURL changes
  useEffect(() => {
    if (capturedImageURL) {
      const img = new Image()
      img.onload = (): void => {
        setSourceRatio(img.width / img.height)
      }
      img.src = capturedImageURL
    }
  }, [capturedImageURL])

  // Attach recorder to canvas when ready
  useEffect((): (() => void) => {
    const canvas = document.querySelector('canvas')
    const canvasContext = canvas?.getContext('bitmaprenderer')
    if (canvasContext && canvas && canvasDimensions) {
      const newCaptureRecorder = new Recorder(canvasContext as RenderingContext, {
        name: `dreambooth-capture`,
        encoderOptions: {
          codec: AVC.getCodec({ profile: 'Baseline', level: '4.1' }),
          bitrate: 2000000
        },
        duration: Infinity,
        download: true,
        frameRate: FPS
      })
      captureCanvasRecorderRef.current = newCaptureRecorder
    }
    return () => {
      captureCanvasRecorderRef.current?.stop()
    }
  }, [canvasDimensions])

  // Calculate image style based on source and display ratios
  const getImageStyle = (): React.CSSProperties => {
    if (!sourceRatio) return { visibility: 'hidden' as const }

    return {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
      position: 'absolute' as const,
      top: '0',
      left: '0'
    }
  }

  // Container style maintains the display ratio
  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    height: '100%',
    aspectRatio: `${displayRatio}`,
    overflow: 'hidden'
  }

  // Initialize timeouts and countdowns
  useEffect((): (() => void) => {
    uploadSessionState()

    // Set up timeout countdown
    timeoutCountdown.setOnTick(() => {
      setRemainingTime(timeoutCountdown.getFormattedRemainingSeconds())
    })

    timeoutCountdown.setOnComplete(() => {
      handleFinish()
    })

    timeoutCountdown.start()

    prepCountdownHook.setOnComplete(() => {
      startCaptureSession()
    })

    // Set up capture countdown
    captureCountdownHook.setOnTick(() => {
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
    })

    // Note: onComplete callback will be set dynamically in runCaptureLoop

    // Handle auto-start when prepTimeout is null/0 (not a retake)
    const prepTimeout = config?.settings.prepTimeout
    const isRetake = userPreferences.retakeCount > 0
    if ((prepTimeout === null || prepTimeout === 0 || prepTimeout === undefined) && !isRetake) {
      // Auto-start immediately - this will trigger credit deduction in startCaptureSession()
      startCaptureSession()
    } else {
      prepCountdownHook.start()
    }

    return (): void => {
      timeoutCountdown.stop()
      prepCountdownHook.stop()
      captureCountdownHook.stop()
      stopCaptureRef.current = true
    }
  }, [])

  const startCaptureSession = async (): Promise<void> => {
    stopCaptureRef.current = false
    
    // Deduct credits only on first capture (not retakes)
    const isFirstCapture = userPreferences.retakeCount === 0
    if (isFirstCapture) {
      // Try to fetch subscription/credits to refresh auth tokens, but don't block session start
      // If offline or fetch fails, proceed anyway - session will queue for upload when online
      const isOnline = navigator.onLine
      if (isOnline) {
        // Online - try to fetch but don't block if it fails
        try {
          await Promise.race([
            fetchSubscriptionAndCredits(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 3000))
          ])
        } catch (error) {
          // If fetch fails or times out, log but don't block session start
          console.warn('[CAPTURE] Failed to fetch subscription/credits, proceeding anyway:', error)
        }
      } else {
        // Offline - start fetchSubscriptionAndCredits in background but don't wait
        fetchSubscriptionAndCredits().catch((error) => {
          console.warn('[CAPTURE] Background fetchSubscriptionAndCredits failed:', error)
        })
      }

      // Upload session state to trigger backend credit deduction
      // Backend will check if session exists, if user has subscription, etc.
      setTimeout(() => {
        uploadSessionState()
      }, 2000)
    }

    logActivity('Capture session started!', {
      captureTime: config?.settings.captureCountdown || 3,
      prepTime: userPreferences.retakeCount > 0 ? 1 : config?.settings.prepTimeout || 5
    })

    try {
      prepCountdownHook.stop()
      setPreparing(false)
      runCaptureLoop()
    } catch (error) {
      console.error('Error in startCaptureSession:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logActivity(`Error in startCaptureSession: ${errorMessage}`, { level: 'error' })
    }
  }

  const runCaptureLoop = async (): Promise<void> => {
    console.log('Starting capture loop with remaining captures:', remainingCapturesRef.current)

    while (remainingCapturesRef.current > 0 && !stopCaptureRef.current) {
      // Get current captured items to determine next ID
      const currentItems = useSessionStateStore.getState().gallery.capturedItems || []
      const captureId = `dreambooth-${currentItems.length + 1}-${Date.now()}`

      console.log('Starting new capture iteration:', {
        remainingCaptures: remainingCapturesRef.current,
        captureId,
        currentItemsCount: currentItems.length
      })

      // --- Start video recording ---
      let captureUrl: string | undefined = undefined
      if (captureCanvasRecorderRef.current) {
        try {
          await captureCanvasRecorderRef.current.start()
        } catch (error) {
          console.error('Error starting capture recorder', error)
        }
      }

      // --- Start the countdown and wait for capture ---
      setCapturing(true)
      // Ensure countdown state is properly set before starting
      const captureTime = config?.settings.captureCountdown || 3
      setCaptureCountdown(captureTime)
      // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
      unifiedCameraUtils.prefocus()
      captureCountdownHook.start()

      // --- Step the recorder during countdown (in parallel) ---
      let frameCount = 0
      const totalFrames = FPS * (config?.settings.captureCountdown || 3)
      const startTime = performance.now()

      // Record frames during countdown (runs in parallel with countdown)
      const recordingPromise = (async (): Promise<void> => {
        while (
          frameCount < totalFrames &&
          captureCanvasRecorderRef.current &&
          captureCanvasRecorderRef.current.status === RecorderStatus.Recording
        ) {
          const currentTime = performance.now()
          const elapsedTime = currentTime - startTime
          const targetFrameTime = (frameCount * 1000) / FPS
          if (elapsedTime >= targetFrameTime) {
            try {
              await captureCanvasRecorderRef.current.step()
              frameCount++
            } catch (error) {
              console.error('Error stepping capture recorder', error)
            }
          }
          await new Promise((resolve) => setTimeout(resolve, 1))
        }
      })()

      const captureCompletionPromise = new Promise<string | undefined>((resolve) => {
        captureCountdownHook.setOnComplete(() => {
          handleCapture().then((url) => {
            resolve(url)
          })
        })
      })

      // Wait for both countdown and recording to complete
      const [imageUrl] = await Promise.all([captureCompletionPromise, recordingPromise])

      // --- Stop and save capture recording ---
      if (
        captureCanvasRecorderRef.current &&
        captureCanvasRecorderRef.current.status === RecorderStatus.Recording
      ) {
        try {
          const captureChunks = await captureCanvasRecorderRef.current.stop()
          const captureBlob = new Blob([captureChunks as ArrayBuffer], { type: 'video/mp4' })
          captureUrl = URL.createObjectURL(captureBlob)
        } catch (error) {
          console.error('Error stopping capture recorder', error)
        }
      }
      if (imageUrl) {
        if (retakingIndex !== null) {
          // Replace existing photo at retaking index
          const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
          const updatedItems = [...prevItems]

          if (updatedItems[retakingIndex]) {
            // Clean up old photo URL
            if (updatedItems[retakingIndex].photoUrl?.startsWith('blob:')) {
              URL.revokeObjectURL(updatedItems[retakingIndex].photoUrl)
            }
            // Replace with new photo and clear filteredVersions
            updatedItems[retakingIndex] = {
              ...updatedItems[retakingIndex],
              photoUrl: imageUrl,
              recordingUrl: captureUrl, // <-- add video
              timestamp: new Date(),
              filteredVersions: {} // Clear old filtered versions
            }
          }

          setSessionState({
            gallery: {
              capturedItems: updatedItems,
              filterCache: {} // Clear all filter caches on retake to prevent stale data
            }
          })

          logActivity(`Retook photo at index ${retakingIndex}`)

          // Start background filter processing for retaken photo
          backgroundFilterProcessor.processAllFiltersForPhoto(
            imageUrl,
            updatedItems[retakingIndex].id
          )

          setRetakingIndex(null)
        } else {
          // Add new captured item to gallery
          const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
          const capturedItem = {
            id: captureId,
            photoUrl: imageUrl,
            recordingUrl: captureUrl, // <-- add video
            timestamp: new Date()
          }

          setSessionState({
            gallery: {
              capturedItems: [...prevItems, capturedItem]
            }
          })

          // Start background filter processing for this photo
          backgroundFilterProcessor.processAllFiltersForPhoto(imageUrl, capturedItem.id)
        }
      }

      // Reset countdown for next capture
      if (remainingCapturesRef.current > 1) {
        console.log('Resetting countdown for next capture')
        const captureTime = config?.settings.captureCountdown || 3
        captureCountdownHook.setRemainingSeconds(captureTime)
        setCaptureCountdown(captureTime) // Reset the state as well
      }
    }

    // Mark all captures as complete
    setAllCapturesComplete(true)

    // Only navigate if the session was not cancelled
    if (stopCaptureRef.current) return

    logActivity('Initial capture session completed.')
    uploadSessionState()
  }

  const handleRetakePhoto = (index: number): void => {
    // Don't allow retake if currently capturing
    if (capturing) return

    logActivity(`Starting retake for photo at index ${index}`)
    setRetakingIndex(index)

    // Start retake capture
    setCapturing(true)
    setCaptureCountdown(initialCaptureCountdown)

    // --- Start video recording for retake ---
    let captureUrl: string | undefined = undefined
    const doRetake = async (): Promise<void> => {
      if (captureCanvasRecorderRef.current) {
        try {
          await captureCanvasRecorderRef.current.start()
        } catch (error) {
          console.error('Error starting capture recorder', error)
        }
      }

      // --- Step the recorder during countdown (in parallel) ---
      let frameCount = 0
      const totalFrames = FPS * initialCaptureCountdown
      const startTime = performance.now()

      // Record frames during countdown (runs in parallel with countdown)
      const recordingPromise = (async (): Promise<void> => {
        while (
          frameCount < totalFrames &&
          captureCanvasRecorderRef.current &&
          captureCanvasRecorderRef.current.status === RecorderStatus.Recording
        ) {
          const currentTime = performance.now()
          const elapsedTime = currentTime - startTime
          const targetFrameTime = (frameCount * 1000) / FPS
          if (elapsedTime >= targetFrameTime) {
            try {
              await captureCanvasRecorderRef.current.step()
              frameCount++
            } catch (error) {
              console.error('Error stepping capture recorder', error)
            }
          }
          await new Promise((resolve) => setTimeout(resolve, 1))
        }
      })()

      const retakePromise = new Promise<string | undefined>((resolve) => {
        captureCountdownHook.setOnComplete(() => {
          handleCapture().then((url) => {
            resolve(url)
          })
        })
      })

      // Wait for both countdown and recording to complete
      const [imageUrl] = await Promise.all([retakePromise, recordingPromise])
      if (
        captureCanvasRecorderRef.current &&
        captureCanvasRecorderRef.current.status === RecorderStatus.Recording
      ) {
        try {
          const captureChunks = await captureCanvasRecorderRef.current.stop()
          const captureBlob = new Blob([captureChunks as ArrayBuffer], { type: 'video/mp4' })
          captureUrl = URL.createObjectURL(captureBlob)
        } catch (error) {
          console.error('Error stopping capture recorder', error)
        }
      }
      if (imageUrl) {
        // Replace existing photo at retaking index
        const prevItems = useSessionStateStore.getState().gallery.capturedItems || []
        const updatedItems = [...prevItems]

        if (updatedItems[index]) {
          // Clean up old photo URL
          if (updatedItems[index].photoUrl?.startsWith('blob:')) {
            URL.revokeObjectURL(updatedItems[index].photoUrl)
          }
          // Replace with new photo and clear filteredVersions
          updatedItems[index] = {
            ...updatedItems[index],
            photoUrl: imageUrl,
            recordingUrl: captureUrl, // <-- add video
            timestamp: new Date(),
            filteredVersions: {} // Clear old filtered versions
          }
        }

        setSessionState({
          gallery: {
            capturedItems: updatedItems
          }
        })

        logActivity(`Retook photo at index ${index}`)

        // Start background filter processing for retaken photo
        backgroundFilterProcessor.processAllFiltersForPhoto(imageUrl, updatedItems[index].id)

        setRetakingIndex(null)
        setCapturing(false)
      }
    }
    captureCountdownHook.setRemainingSeconds(initialCaptureCountdown)
    setCaptureCountdown(initialCaptureCountdown) // Reset the state as well
    // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
    unifiedCameraUtils.prefocus()
    captureCountdownHook.start()
    doRetake()
  }

  const handleCapture = async (): Promise<string | undefined> => {
    try {
      console.log('Starting capture process')

      setSessionState({
        // Stats tracked by Gallery API
      })

      flashEffect('.flash')

      console.log('Capture settings:', {
        selectedCamera: userPreferences.selectedCamera,
        useShutterCapture: cameraSettings.useShutterCapture,
        captureRatio: cameraSettings.captureRatio
      })

      await new Promise((resolve) => setTimeout(resolve, 200))

      const imageData = await unifiedCameraUtils.captureImage(
        userPreferences.selectedCamera as 'primary' | 'secondary',
        undefined, // No filter applied during capture
        getNormalizedCaptureRatio(),
        !cameraSettings.useShutterCapture
      )

      if (imageData) {
        console.log('Successfully captured image')
        const imageUrl = URL.createObjectURL(imageData)
        setCapturedImageURL(imageUrl)

        if (downloadLocal) {
          const a = document.createElement('a')
          a.href = imageUrl
          a.download = `dreambooth-${galleryKey || gallery.id}-${Date.now()}.jpg`
          a.setAttribute('data-automated-click', 'true')
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)

          logActivity(`Downloaded photo`, {
            type: 'photo',
            filename: `dreambooth-${galleryKey || gallery.id}-${Date.now()}.jpg`
          })
        }

        setCapturing(false)

        // Only decrement remaining captures if it's not a retake
        if (retakingIndex === null) {
          remainingCapturesRef.current--
        }

        return imageUrl
      }

      logActivity('Capture processing failed: No image data captured.', { level: 'error' })
      return undefined
    } catch (error) {
      console.error('Capture failed:', error)
      setCapturing(false)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      logActivity(`Capture processing failed: ${errorMessage}`, { level: 'error' })
      return undefined
    }
  }

  // Get frame configuration
  const getFrameConfig = (frame: FrameOption | null): FrameConfig => {
    if (!frame)
      return {
        id: '',
        baseImage: '',
        drawParams: {
          canvasWidth: 1200,
          canvasHeight: 1800,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }
    return {
      id: frame._id,
      baseImage: frame.thumbnail,
      drawParams: frame.drawParams
    }
  }

  // Get frame
  const getFrame = (frameId: string): FrameOption | null => {
    return frames.find((frame) => frame._id === frameId) || null
  }

  const handleFinish = (): void => {
    // Check if we need to continue with secondary camera capture
    const currentStep = userPreferences.currentCaptureStep || 0
    const captureSequence = userPreferences.captureSequence || ['primary']

    if (currentStep < captureSequence.length - 1) {
      // Move to next camera in sequence
      const nextStep = currentStep + 1
      const nextCamera = captureSequence[nextStep]

      setSessionState({
        userPreferences: {
          currentCaptureStep: nextStep,
          selectedCamera: nextCamera
        }
      })

      logActivity(`Moving to ${nextCamera} camera capture`, {
        currentStep: nextStep,
        totalSteps: captureSequence.length
      })

      uploadSessionState()
      window.location.reload() // Reload for next camera
    } else {
      logActivity('All capture sessions completed')
      // Generate live mode video using neutral photos (unfiltered)
      const neutralPhotos = gallery.capturedItems.filter((item) => item.photoUrl)
      const resources = neutralPhotos.map((item) => ({
        photoUrl: item.photoUrl,
        videoUrl: item.recordingUrl || ''
      }))
      console.log('Resources:', resources)
      setSessionState({
        gallery: {
          ...gallery,
          liveModeProcessing: true,
          liveModeUrl: null,
          liveModeLocalUrl: undefined,
          liveModeRemoteUrl: undefined
        }
      })
      backgroundVideoProcessor.processVideo({
        id: `live-mode-${gallery.id}`,
        type: 'live',
        resources,
        frameConfig: getFrameConfig(getFrame(userPreferences.frameId || frames[0]._id)),
        // The session id, not gallery.id (the galleryKey), so the live video
        // lands in the gallery doc the guest link opens.
        sessionId: useSessionStateStore.getState().id || gallery.id,
        userId: userId || '',
        projectId: projectId || '',
        galleryId: gallery.id,
        speed: globalConfig?.pages.capture.settings.recordingSpeed || 1,
        fps: 30,
        // photoDuration is ignored in Live Mode (video-only), kept for backward compatibility
        photoDuration: 0,
        onComplete: (uploadedUrl: string, localUrl?: string) => {
          const { playbackUrl, videoRemoteUrl } = resolveFramedVideoPlaybackAndRemote(
            uploadedUrl,
            localUrl
          )
          setSessionState({
            gallery: {
              ...gallery,
              liveModeProcessing: false,
              liveModeUrl: playbackUrl || null,
              liveModeLocalUrl: localUrl,
              liveModeRemoteUrl: videoRemoteUrl
            }
          })
        },
        onError: (error) => {
          setSessionState({
            gallery: {
              ...gallery,
              liveModeProcessing: false,
              liveModeUrl: null,
              liveModeLocalUrl: undefined,
              liveModeRemoteUrl: undefined
            }
          })
          logActivity('Live mode video generation failed', { error: error.message })
        }
      })
      uploadSessionState()
      navigate(`/${projectId}/filter`)
    }
  }

  // Generate photo placeholder grid based on layout
  const generatePhotoGrid = (): React.ReactElement[] => {
    // FIX: Use reactive state instead of getState()
    const currentItems = gallery.capturedItems || []
    const currentProgress = captureCount - remainingCapturesRef.current
    const photoSlots: React.ReactElement[] = []

    // Determine how many slots to show based on layout
    let totalSlots = 0
    if (photoLayoutInfo.layout === 'vertical') {
      // For 3cut and 4cut2: each photo appears twice (side by side)
      totalSlots = captureCount * 2
    } else {
      // For 4cut1: each photo appears once in grid
      totalSlots = captureCount
    }

    for (let i = 0; i < totalSlots; i++) {
      let photoIndex: number
      let isCaptured = false
      let isCurrentlyCapturing = false
      let isRetaking = false
      let photoItem: { id: string; photoUrl: string; timestamp: Date } | null = null

      if (photoLayoutInfo.layout === 'vertical') {
        // For vertical layouts (3cut, 4cut2): 1 1, 2 2, 3 3, (4 4)
        photoIndex = Math.floor(i / 2) // 0,0,1,1,2,2,(3,3)
        isCaptured = photoIndex < currentItems.length
        isCurrentlyCapturing = photoIndex === currentProgress && capturing && retakingIndex === null
        isRetaking = retakingIndex === photoIndex && capturing
        photoItem = isCaptured ? currentItems[photoIndex] : null
      } else {
        // For grid layout (4cut1): 1 2, 3 4
        photoIndex = i // 0,1,2,3
        isCaptured = photoIndex < currentItems.length
        isCurrentlyCapturing = photoIndex === currentProgress && capturing && retakingIndex === null
        isRetaking = retakingIndex === photoIndex && capturing
        photoItem = isCaptured ? currentItems[photoIndex] : null
      }

      photoSlots.push(
        <div
          key={`slot-${i}-${photoItem?.id || ''}-${photoItem?.timestamp?.getTime?.() || ''}`}
          className={`relative border-3 rounded-lg overflow-hidden transition-all duration-300 ${
            isCurrentlyCapturing
              ? 'border-blue-400 bg-blue-50'
              : isCaptured
                ? 'border-green-300 bg-gray-50'
                : 'border-gray-300 bg-gray-50'
          }`}
          style={containerStyle}
        >
          {photoItem?.photoUrl ? (
            <>
              <img
                src={photoItem.photoUrl}
                alt={`Photo ${photoIndex + 1}`}
                style={getImageStyle()}
                crossOrigin="anonymous"
              />

              {/* Retaking Overlay */}
              {isRetaking && (
                <div className="absolute inset-0 bg-black/70 flex items-center justify-center border-3 border-blue-400 rounded-lg">
                  <span className="text-white text-lg font-bold">Retaking...</span>
                </div>
              )}

              {/* Retake Button - Show when all captures complete and not currently capturing */}
              {allCapturesComplete && !capturing && !isRetaking && (
                <button
                  onClick={() => handleRetakePhoto(photoIndex)}
                  className="absolute top-3 right-3 w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/50 transition-colors shadow-lg"
                  aria-label={`Retake photo ${photoIndex + 1}`}
                >
                  <FaUndo className="text-black text-base" />
                </button>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {isCurrentlyCapturing ? (
                <span className="text-blue-500 text-lg font-bold">Capturing...</span>
              ) : (
                <span className="text-gray-400 text-2xl font-bold">{photoIndex + 1}</span>
              )}
            </div>
          )}
        </div>
      )
    }

    return photoSlots
  }

  // Display progress - FIX: Use reactive state
  const captureProgress = {
    current: gallery.capturedItems?.length || 0,
    total: captureCount
  }

  return (
    <div className="relative w-full h-full bg-white">
      <div id="flash" className="w-full h-full flash"></div>

      {/* Timeout Display */}
      <div className="absolute top-8 right-8 z-20">
        <div className="w-20 h-20 border-4 border-redYotta rounded-full flex items-center justify-center">
          <span className="text-redYotta text-sm font-bold">{remainingTime}</span>
        </div>
      </div>

      <div className="w-full h-full flex">
        {/* Left Area - Camera Preview */}
        <div className="w-2/3 relative">
          {/* Capture Countdown Overlay */}
          {capturing && (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <span
                key={captureCountdown}
                className="text-[200px] text-white animate-countdown drop-shadow-lg"
              >
                {captureCountdown}
              </span>
            </div>
          )}

          {/* Get Ready Overlay */}
          {preparing && (
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70 flex flex-col z-[3] items-center justify-center">
              <div className="flex flex-col items-center gap-8 max-w-2xl text-center px-8">
                <h1 className="font-title-yotta text-blueGrayYotta text-[90px] font-bold drop-shadow-lg">
                  Get Ready!
                </h1>
                <p className="text-2xl text-blueGrayYotta">
                  Camera: {userPreferences.cameraMode === 'high' ? 'High' : 'Basic'}
                </p>
                <button
                  onClick={startCaptureSession}
                  className="font-body-yotta text-blueGrayYotta text-[32px] hover:opacity-80 transition-opacity bg-white px-8 py-4 rounded-lg shadow-lg"
                >
                  Start Now
                </button>
              </div>
            </div>
          )}

          {/* Completion Overlay */}
          {allCapturesComplete && !capturing && (
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70 flex flex-col z-[3] items-center justify-center">
              <div className="flex flex-col items-center gap-8 max-w-2xl text-center px-8">
                <h1 className="font-title-yotta text-blueGrayYotta text-[90px] font-bold drop-shadow-lg">
                  All Done!
                </h1>
                <p className="text-2xl text-blueGrayYotta">
                  You can retake photos or proceed to the next step
                </p>
                <p className="text-lg text-blueGrayYotta opacity-80">
                  Time remaining: {remainingTime}
                </p>
                <button
                  onClick={handleFinish}
                  className="font-body-yotta text-blueGrayYotta text-[32px] hover:opacity-80 transition-opacity bg-white px-8 py-4 rounded-lg shadow-lg"
                >
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* Camera Preview */}
          <div className="w-full h-full">
            <Preview
              adjustments={undefined}
              ratio={displayRatio}
              cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
              filter={undefined}
              onCanvasReady={setCanvasDimensions}
            />
          </div>
        </div>

        {/* Right Area - Photo Grid */}
        <div className="w-1/3 p-6 flex flex-col gap-4">
          {/* Progress Counter */}
          <div className="mt-12">
            <p className="text-blueGrayYotta text-2xl font-bold">
              {captureProgress.current}/{captureProgress.total} taken
            </p>
          </div>

          {/* Photo Grid */}
          <div className="flex-1 relative mb-6">
            <div
              className={
                photoLayoutInfo.layout === 'vertical'
                  ? 'grid grid-cols-2 gap-4 h-full auto-rows-fr' // Vertical layout: 1 1, 2 2, 3 3, 4 4
                  : 'grid grid-cols-2 gap-4 h-full auto-rows-fr' // Grid layout: 1 2, 3 4
              }
            >
              {generatePhotoGrid()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Capture
